// tslint:disable:no-console no-unused-expression
import { fabric } from "fabric"
import env from "@/common/env"
import { round } from "@/common/util"

const logging = !env.PRODUCTION && !env.TEST && !env.isJSDOM
const hasPerformance = logging && typeof window !== "undefined" && window.performance && performance.mark

export const log = (msg: string) => {
  logging && console.log(`Signomatic: ${msg}`)
}

export const info = (msg: string) => {
  logging && console.info(`Signomatic: ${msg}`)
}

export const warn = (msg: string) => {
  logging && console.warn(`Signomatic: ${msg}`)
}

export const error = (msg: string) => {
  logging && console.error(`Signomatic: ${msg}`)
}

let loadStart = 0

export const init = () => {
  if (!hasPerformance) {
    return
  }

  loadStart = performance.now()
  info("started loading Signomatic")
}

export const finished = () => {
  if (!hasPerformance) {
    return
  }

  const loadEnd = performance.now()
  info(
    `finished loading version ${env.VERSION.substr(0, 7)} with Fabric ${fabric.version} in ${round(
      loadEnd - loadStart
    )}ms`
  )
}

export const timingMarkStart = (name: string) => {
  if (!hasPerformance) {
    return
  }

  performance.mark(`${name}-mark-start`)
}

export const timingMarkEnd = (name: string) => {
  if (!hasPerformance) {
    return
  }

  performance.mark(`${name}-mark-end`)
  performance.measure(name, `${name}-mark-start`, `${name}-mark-end`)
}

/**
 * Decorator that profiles the method call and prints the timing to console
 */
// tslint:disable-next-line:only-arrow-functions
export const profile = function (warnLimit?: number) {
  // tslint:disable-next-line:only-arrow-functions
  return function (target: any, key: string, descriptor: PropertyDescriptor) {
    const original = descriptor.value

    descriptor.value = function () {
      const args = []
      for (let i = 0; i < arguments.length; i++) {
        args[i - 0] = arguments[i] // eslint-disable-line prefer-rest-params
      }

      if (hasPerformance) {
        const label = `${target.constructor.name}:${key}`
        const start = performance.now()

        // call function
        const result = original.apply(this, args)

        const end = performance.now()
        log(`${label} took ${round(end - start)}ms`)

        if (warnLimit && end - start > warnLimit) {
          warn(`${label} took more than ${warnLimit}ms!`)
        }

        return result
      }

      return original.apply(this, args)
    }

    return descriptor
  }
}

export default {
  init,
  finished,
  log,
  warn,
  info,
  error,
  profile,
}
